import React, { FC, useCallback, useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';
import { debounce } from 'lodash';
import { styled } from 'linaria/react';
import { PaymentFormProps, SteamLoginField, SteamAmountField, SteamFinalAccountAmount, PaySection, checkIsPaymentAvailable, MtsPaymentMethodCard } from '@features/payment/payment-form';
import { PaymentMethodSwitcher } from '@features/payment/method-switcher';
import { SteamAccountCountrySwitcher } from '@features/country/select-country';
import { SteamPromocodeField } from '@features/promocode/activate';
import { PaymentFormValues, PaymentMethod } from '@entities/payment';
import { useCyberStore } from '@entities/tsp';
import { useAuthDataStore, useBindingsStore } from '@entities/user';
import { getSteamAccountFinalAmount } from '@entities/price';
import { withTheme } from '@shared/lib/theme';
import { Mobile, useBreakpoint } from '@shared/lib/react';
import { useScrollDirection } from '@shared/lib/browser';
import { Partners, usePartner } from '@shared/lib/partner';
import { isChromeOrEdge, NoSSR, useIntersectionObserver } from '@shared/lib';
import { InfoBanner } from '@shared/ui/molecules';
import { Link, Text } from '@shared/ui/atoms';
import { SbpIcon } from '@shared/assets/icons';
import WhereToFindSteamLoginModalDesktop from './where-to-find-steam-login-modal/WhereToFindSteamLoginModalDesktop';
import WhereToFindSteamLoginModalMobile from './where-to-find-steam-login-modal/WhereToFindSteamLoginModalMobile';
import AmountInfo from './AmountInfo';
const SteamFormSection: FC<PaymentFormProps> = ({
  payee,
  getCommissions,
  isPaymentloading,
  isAmountInfoLoading
}) => {
  const {
    values,
    errors
  } = useFormikContext<PaymentFormValues>();
  const {
    partner
  } = usePartner();
  const {
    paymentMethod,
    amountInfo
  } = useCyberStore();
  const {
    isAuth,
    operatorMTS,
    getPhone,
    paymentBanMTS,
    errorOperatorInfo
  } = useAuthDataStore();
  const {
    bindings
  } = useBindingsStore();
  const {
    isMobile
  } = useBreakpoint();
  const [paySectionRef, isPaySectionVisible] = useIntersectionObserver<HTMLDivElement>();
  const steamLoginFieldRef = useRef<HTMLDivElement | null>(null);
  const {
    scrollDirection,
    isAtTop
  } = useScrollDirection();
  const isNavbarHidden = scrollDirection === 'down' && !isAtTop;
  const debouncedGetCommission = useCallback(debounce((price: number) => {
    if (price) {
      getCommissions(getSteamAccountFinalAmount(price));
    }
  }, 500, {
    leading: false,
    trailing: true
  }), [paymentMethod, values, errors]);
  const setFieldRef = useCallback((el: HTMLDivElement | null) => {
    const input = el?.querySelector('input');
    steamLoginFieldRef.current = input || el;
  }, []);
  useEffect(() => {
    if (amountInfo) {
      debouncedGetCommission(Number(values?.price));
    } else {
      getCommissions(getSteamAccountFinalAmount(Number(values?.price)));
    }
  }, [values?.price]);
  const isPaymentAvailable = checkIsPaymentAvailable({
    paymentMethod,
    bindings,
    phone: getPhone(),
    isAuth,
    paymentBanMTS,
    operatorMTS,
    errorOperatorInfo
  });
  const isLoginFilled = Boolean(values.login?.trim());
  const shouldShowStickyPayButton = !isPaySectionVisible && isLoginFilled;
  return <SteamFormSectionWrapper>
      <AccountDataWrapper>
        <Text family="MTSCompact" weight="medium" size={{
        desktop: 'h6',
        mobile: 'pL'
      }} color="primary">
          Данные аккаунта
        </Text>
        <SteamAccountCountrySwitcherWrapper>
          <SteamAccountCountrySwitcher />
        </SteamAccountCountrySwitcherWrapper>
        <FieldsWrapper>
          <SteamLoginField ref={setFieldRef} getCommissions={getCommissions} renderSteamLoginModal={({
          isModalActive,
          handleModalClose
        }) => isMobile ? <WhereToFindSteamLoginModalMobile open={isModalActive} onClose={handleModalClose} /> : <WhereToFindSteamLoginModalDesktop open={isModalActive} onClose={handleModalClose} />} />
          <SteamAmountField />
          <SteamFinalAccountAmount />
        </FieldsWrapper>
      </AccountDataWrapper>
      <ReplenishmentMethodWrapper>
        {partner === Partners.DEFAULT ? <NoSSR>
            <Text family="MTSCompact" weight="medium" size={{
          desktop: 'h6',
          mobile: 'pL'
        }} color="primary">
              Способ оплаты
            </Text>
            <PaymentMethodSwitcher payee={payee} />
            {paymentMethod === PaymentMethod.MTS_ACCOUNT && isAuth && <MtsPaymentMethodCard />}
            {isPaymentAvailable && <SteamPromocodeField getCommissions={getCommissions} />}
          </NoSSR> : <PaymentBySBPWrapper>
            <Text family="MTSCompact" weight="medium" size={{
          desktop: 'h6',
          mobile: 'pL'
        }} color="primary">
              К оплате
            </Text>

            <Text family="MTSCompact" size="pS" weight="medium" color="secondary" style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
              Оплата по «Системе быстрых платежей» <SbpIcon />
            </Text>
          </PaymentBySBPWrapper>}
      </ReplenishmentMethodWrapper>
      {isPaymentAvailable && <AmountInfoWrapper>
          <AmountInfo isVouchers={false} payee={payee} isGetPaymentTermsLoading={isAmountInfoLoading} />
        </AmountInfoWrapper>}
      {isChromeOrEdge() && <NoSSR>
          <InfoBannerWrapper>
            <InfoBanner>
              Оплата проходит стабильнее в&nbsp;Яндекс&nbsp;Браузере или&nbsp;с&nbsp;
              <Link href="https://www.gosuslugi.ru/crt">сертификатами Минцифры</Link>
            </InfoBanner>
          </InfoBannerWrapper>
        </NoSSR>}
      <PaySectionWrapper ref={paySectionRef}>
        <PaySection payee={payee} isPaymentloading={isPaymentloading} isAmountInfoLoading={isAmountInfoLoading} />
      </PaySectionWrapper>
      {shouldShowStickyPayButton && <Mobile>
          <StickyWrapper isNavbarHidden={isNavbarHidden}>
            <PaySection payee={payee} isPaymentloading={isPaymentloading} isAmountInfoLoading={isAmountInfoLoading} />
          </StickyWrapper>
        </Mobile>}
    </SteamFormSectionWrapper>;
};
export default SteamFormSection;
const _exp = /*#__PURE__*/() => ({
  theme
}) => theme.colors.background.secondary;
const SteamFormSectionWrapper = withTheme(/*#__PURE__*/styled('div')({
  name: "SteamFormSectionWrapper",
  class: "s14wu51z",
  propsAsIs: false,
  vars: {
    "s14wu51z-0": [_exp()]
  }
}));
const AccountDataWrapper = /*#__PURE__*/styled('div')({
  name: "AccountDataWrapper",
  class: "a1wbqq8t",
  propsAsIs: false
});
const SteamAccountCountrySwitcherWrapper = /*#__PURE__*/styled('div')({
  name: "SteamAccountCountrySwitcherWrapper",
  class: "s12wyibv",
  propsAsIs: false
});
const FieldsWrapper = /*#__PURE__*/styled('div')({
  name: "FieldsWrapper",
  class: "f1kbjkhi",
  propsAsIs: false
});
const ReplenishmentMethodWrapper = /*#__PURE__*/styled('div')({
  name: "ReplenishmentMethodWrapper",
  class: "r1fmjvw3",
  propsAsIs: false
});
const PaymentBySBPWrapper = /*#__PURE__*/styled('div')({
  name: "PaymentBySBPWrapper",
  class: "p1on2sju",
  propsAsIs: false
});
const AmountInfoWrapper = /*#__PURE__*/styled('div')({
  name: "AmountInfoWrapper",
  class: "a1flxrki",
  propsAsIs: false
});
const InfoBannerWrapper = /*#__PURE__*/styled('div')({
  name: "InfoBannerWrapper",
  class: "ios9182",
  propsAsIs: false
});
const PaySectionWrapper = /*#__PURE__*/styled('div')({
  name: "PaySectionWrapper",
  class: "pjxnh9w",
  propsAsIs: false
});
interface StickyWrapperProps {
  isNavbarHidden: boolean;
}
const _exp2 = /*#__PURE__*/() => ({
  isNavbarHidden
}) => isNavbarHidden ? 0 : '74px';
const _exp3 = /*#__PURE__*/() => ({
  theme
}) => theme.colors.background.primary;
const StickyWrapper = withTheme(/*#__PURE__*/styled('div')({
  name: "StickyWrapper",
  class: "stnsnfs",
  propsAsIs: false,
  vars: {
    "stnsnfs-0": [_exp2()],
    "stnsnfs-1": [_exp3()]
  }
}));

require("./SteamFormSection.linaria.module.css!=!../../../../node_modules/linaria/node_modules/@linaria/webpack4-loader/lib/outputCssLoader.js?cacheProvider=!./SteamFormSection.tsx");